import { forwardRef, useEffect, useRef, useState } from 'react';
import type { Ref, PropsWithoutRef, RefObject } from 'react';
import styled, { css } from 'styled-components';

import Actions from '../Actions';
import Flex from '../Flex';
import EmptyState from '../EmptyState';
import SearchInput, { StyledSearchInput } from '../SearchInput';
import type { SearchInputProps } from '../SearchInput';
import { defaultThemeProp } from '../../theme';
import Progress from '../Progress';
import { StyledCard } from '../Card';
import { StyledCardContent } from '../Card/CardContent';
import { omitProps } from '../../styles';
import { usePrevious, useI18n } from '../../hooks';
import type { ForwardRefForwardPropsComponent } from '../../types';

import SummaryList from './SummaryList';
import type { SummaryListProps } from './SummaryList';

export interface ViewAllProps
  extends Pick<SummaryListProps, 'name' | 'loading' | 'actions' | 'items'> {
  searchInputProps?: SearchInputProps;
  /** ID of the item that was just deleted. Setting this triggers post-delete focus management. */
  deletedItemId?: string;
  /** Called after focus has been managed post-delete so the consumer can clear deletedItemId. */
  onDeleteFocusComplete?: () => void;
  /** Ref to the fallback element (e.g. Add button) to focus when the list becomes empty after deletion. */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
  ref?: Ref<HTMLDivElement>;
}

const StyledToolbar = styled.div(({ theme: { base } }) => {
  return css`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${base.palette['primary-background']};
    margin-bottom: ${base.spacing};
    transform: translateY(calc(-0.5 * ${base.spacing}));
    padding: ${base.spacing} 0;

    ${StyledSearchInput} {
      flex-grow: 1;
    }
  `;
});

StyledToolbar.defaultProps = defaultThemeProp;

export const StyledViewAll = styled.div.withConfig(omitProps('loading'))<{
  loading: ViewAllProps['loading'];
}>(
  ({ loading }) => css`
    ${StyledCardContent}:not(${StyledCard} ${StyledCard} > ${StyledCardContent}) {
      padding: 0;
    }

    ${loading &&
    css`
      min-height: 8rem;
    `}
  `
);

const SearchableList: ForwardRefForwardPropsComponent<ViewAllProps> = forwardRef(
  function SearchableList(
    {
      name,
      loading,
      actions,
      items,
      searchInputProps,
      deletedItemId,
      onDeleteFocusComplete,
      fallbackFocusRef,
      ...restProps
    }: PropsWithoutRef<ViewAllProps>,
    ref: ViewAllProps['ref']
  ) {
    const t = useI18n();
    const [searchValue, setSearchValue] = useState<SearchInputProps['value']>('');
    const prevItemsLength = usePrevious(items.length);
    const hasProcessedEmptyRef = useRef(false);
    const toolbarActionsRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (!deletedItemId) {
        hasProcessedEmptyRef.current = false;
        return;
      }
      if (hasProcessedEmptyRef.current) return;
      if (items.length === 0 && (prevItemsLength ?? 0) > 0) {
        hasProcessedEmptyRef.current = true;
        const el = fallbackFocusRef?.current ?? toolbarActionsRef.current;
        el?.focus();
        onDeleteFocusComplete?.();
      }
    }, [deletedItemId, items.length]);

    return (
      <StyledViewAll ref={ref} loading={loading} {...restProps}>
        {!loading && (searchInputProps || actions) && (
          <Flex container={{ justify: 'between', alignItems: 'center', gap: 2 }} as={StyledToolbar}>
            {searchInputProps && (
              <Flex container={{ alignItems: 'center', gap: 1 }} item={{ grow: 1 }}>
                <SearchInput
                  onSearchChange={val => {
                    searchInputProps.onSearchChange?.(val);
                    setSearchValue(val);
                  }}
                  searchInputAriaLabel={name ? t('search_noun', [name]) : t('search')}
                  value={searchInputProps.value || searchValue}
                />
              </Flex>
            )}
            {actions && (
              <Actions
                ref={toolbarActionsRef}
                contextualLabel={name}
                iconOnly={false}
                items={actions}
              />
            )}
          </Flex>
        )}

        {loading && <Progress placement='local' />}
        {!loading &&
          (items.length ? (
            <SummaryList
              name={name}
              items={items}
              deletedItemId={deletedItemId}
              onDeleteFocusComplete={onDeleteFocusComplete}
              fallbackFocusRef={fallbackFocusRef}
              hideHeader
            />
          ) : (
            <EmptyState />
          ))}
      </StyledViewAll>
    );
  }
);

export default SearchableList;
